import { useState, type FormEvent } from 'react'
import { Eye, EyeOff, KeyRound, LogIn, User } from 'lucide-react'
import { loginSchema } from '@najah/core'
import { t } from '@najah/i18n'
import { Alert, Button, Input, LogoMark } from '@najah/ui'
import { ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'

export function LoginPage() {
  const { login } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFormError(null)

    // نتحقّق محلياً أولاً بنفس مخطط الخادم — رسائل فورية بلا رحلة شبكة
    const parsed = loginSchema.safeParse({ username, password })
    if (!parsed.success) {
      const errors: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '_')
        errors[key] ??= issue.message
      }
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    setSubmitting(true)
    try {
      await login(parsed.data)
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.fields) setFieldErrors(err.fields)
        else setFormError(err.message)
      } else {
        setFormError(t.errors.unknown)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_1.1fr]">
      {/* ── نموذج الدخول ── */}
      <div className="flex items-center justify-center bg-bg px-6 py-12">
        <div className="w-full max-w-sm animate-[fade-in_0.3s_var(--ease-out-quint)]">
          <div className="mb-8 flex flex-col items-center text-center lg:items-start lg:text-start">
            <LogoMark className="size-16" />
            <h1 className="mt-4 font-display text-2xl font-bold text-fg">{t.auth.welcomeBack}</h1>
            <p className="mt-1 text-sm text-fg-muted">{t.auth.loginSubtitle}</p>
          </div>

          {formError && (
            <Alert tone="danger" className="mb-5">
              {formError}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <Input
              label={t.auth.username}
              placeholder={t.auth.usernamePlaceholder}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              error={fieldErrors.username}
              startIcon={<User className="size-4" />}
              autoComplete="username"
              autoFocus
              ltr
              required
            />

            <Input
              label={t.auth.password}
              placeholder={t.auth.passwordPlaceholder}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={fieldErrors.password}
              startIcon={<KeyRound className="size-4" />}
              endIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="pointer-events-auto rounded p-0.5 text-fg-subtle transition-colors hover:text-fg"
                  aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              }
              autoComplete="current-password"
              ltr
              required
            />

            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={submitting}
              startIcon={<LogIn className="size-4 icon-flip" />}
              className="mt-2"
            >
              {submitting ? t.auth.signingIn : t.auth.login}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-fg-subtle lg:text-start">
            {t.app.name} — {t.app.tagline}
          </p>
        </div>
      </div>

      {/* ── اللوحة التعريفية (تختفي على الشاشات الصغيرة) ── */}
      <div className="relative hidden overflow-hidden bg-primary lg:block">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
          aria-hidden
        />
        <div
          className="absolute -top-24 -start-24 size-96 rounded-full bg-accent/20 blur-3xl"
          aria-hidden
        />
        <div
          className="absolute -bottom-32 -end-16 size-[28rem] rounded-full bg-white/10 blur-3xl"
          aria-hidden
        />

        <div className="relative flex h-full flex-col justify-between p-12">
          <div />
          <div className="max-w-md">
            <h2 className="font-display text-4xl font-bold leading-[1.3] text-white">
              نظام متكامل لإدارة
              <br />
              المكتبة والقرطاسية
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-white/75">
              المخزون، نقطة البيع، الطلبات، المشتريات والتقارير — في مكان واحد،
              ويعمل حتى دون اتصال بالإنترنت.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                'بيع وفوترة سريعة بالباركود',
                'مخزون دقيق بسجل حركات كامل',
                'طلبات الزبائن من تطبيق الموبايل',
                'تقارير مبيعات وأرباح لحظية',
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-white/85">
                  <span className="size-1.5 shrink-0 rounded-full bg-accent" aria-hidden />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-white/40">الإصدار 0.1.0</p>
        </div>
      </div>
    </div>
  )
}
